import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { usePermissions } from '../contexts/PermissionContext';
import {
  Search,
  Button,
  Tag,
  SkeletonPlaceholder,
  SkeletonText,
  TextInput,
  Select,
  SelectItem,
  InlineNotification,
  ToastNotification,
  Modal,
  FileUploader
} from '@carbon/react';
import { Add, Close, ChevronLeft, ChevronRight, TrashCan, Edit, Filter, Checkmark, OverflowMenuHorizontal, UserMultiple } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import './EmployeesTable.css';

// Custom Overflow Menu - same design/behaviour as the Residents list toolbar
const OverflowMenu = ({ isOpen, onToggle, onClose, menuRef, children, dark = false }) => {
  const menuItemsRef = useRef([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const handleKeyDown = (e) => {
    const items = menuItemsRef.current.filter(Boolean);

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        onClose?.();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => (prev - 1 + items.length) % items.length);
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'Tab':
        onClose?.();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && menuItemsRef.current[focusedIndex]) {
      menuItemsRef.current[focusedIndex].focus();
    }
  }, [focusedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFocusedIndex(-1);
    }
  }, [isOpen]);

  return (
    <div className={`employees-overflow-menu ${dark ? 'employees-overflow-menu--dark' : ''}`} ref={menuRef}>
      <button
        className="employees-overflow-trigger"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Više opcija"
        title="Više opcija"
      >
        <OverflowMenuHorizontal />
      </button>
      {isOpen && (
        <div
          className="employees-overflow-dropdown"
          role="menu"
          aria-label="Dodatne akcije"
          onKeyDown={handleKeyDown}
        >
          {/* `toArray` + `isValidElement` uklanja null/false decu (uslovno
              renderovane stavke). */}
          {React.Children.toArray(children)
            .filter((child) => React.isValidElement(child))
            .map((child, index) =>
              React.cloneElement(child, {
                ref: (el) => (menuItemsRef.current[index] = el),
                tabIndex: focusedIndex === index ? 0 : -1
              })
            )}
        </div>
      )}
    </div>
  );
};

const OverflowMenuItem = React.forwardRef(({ icon: Icon, children, onClick, variant = 'default', tabIndex = -1 }, ref) => (
  <button
    ref={ref}
    className={`employees-overflow-item ${variant === 'danger' ? 'employees-overflow-item--danger' : ''}`}
    onClick={onClick}
    role="menuitem"
    tabIndex={tabIndex}
  >
    {Icon && <span className="employees-overflow-item__icon"><Icon /></span>}
    <span className="employees-overflow-item__label">{children}</span>
  </button>
));
OverflowMenuItem.displayName = 'OverflowMenuItem';

const EmployeesTable = ({ inactiveOnly = false }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDarkMode = theme === 'g100';

  // Dodavanje/izmena zaposlenih zahteva `employees.manage`. Korisnik kome je
  // dodeljeno samo upravljanje smenama (`shifts.manage`) vidi listu zaposlenih,
  // ali bez akcija za dodavanje (na profilu vidi samo tab „Smene").
  const { can } = usePermissions();
  const canManageEmployees = can('employees.manage');

  // Da li je desktop viewport (>= 769px). Na mobilnom (< 769px) toolbar se
  // drugačije raspoređuje: nema dugmeta "Dodaj zaposlenog", "tri tačke" stoje
  // u redu za pretragu, a paginacija ide ispod liste.
  const [isDesktopView, setIsDesktopView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(min-width: 769px)').matches
      : true
  );

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState(searchParams.get('employeeTypeId') || '');
  const [areaFilter, setAreaFilter] = useState('');
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [pageFading, setPageFading] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFilterExiting, setIsFilterExiting] = useState(false);
  const [step, setStep] = useState(1);
  const [editSection, setEditSection] = useState('identity');

  // Form state
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    address: '',
    hireDate: '',
    employeeTypeId: '',
    imgSrc: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Overflow menu (tri tačke) state
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const overflowMenuRef = useRef(null);

  const extractData = (res) => {
    const body = res.data;
    if (Array.isArray(body)) return body;
    if (body && Array.isArray(body.data)) return body.data;
    if (body && Array.isArray(body.value)) return body.value;
    if (body && Array.isArray(body.$values)) return body.$values;
    if (body && Array.isArray(body.items)) return body.items;
    // Handle { success, data: { employees: [...] } } structure
    if (body && body.data && Array.isArray(body.data.employees)) return body.data.employees;
    if (body && body.data && Array.isArray(body.data.items)) return body.data.items;
    if (body && body.data && Array.isArray(body.data.$values)) return body.data.$values;
    if (body && body.data && Array.isArray(body.data.value)) return body.data.value;
    return [];
  };

  // Učitaj podatke sa API-ja (server-side paging, search i filteri)
  const fetchEmployees = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        // "Bivši" (neaktivni) zaposleni imaju poseban endpoint, ali su parametri
        // i format odgovora identični standardnoj listi (GET /api/Employee).
        const endpoint = inactiveOnly ? '/api/Employee/inactive' : '/api/Employee';

        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (employeeTypeFilter) params.append('employeeTypeId', employeeTypeFilter);
        if (areaFilter) params.append('areaId', areaFilter);
        params.append('pageNumber', String(page));
        params.append('pageSize', String(pageSize));

        const response = await axiosInstance.get(`${endpoint}?${params.toString()}`);
        console.log('API Response:', response);
        console.log('Response data:', response.data);

        const data = extractData(response);
        setEmployees(data);

        // Pagination metadata - check multiple possible response structures
        const body = response.data;
        const pagination =
          body?.pagination ||
          body?.data?.pagination ||
          body?.value?.pagination ||
          body?.$values?.pagination ||
          body?.items?.pagination ||
          body?.meta ||
          body?.data?.meta;

        if (pagination?.totalPages) {
          setTotalPages(pagination.totalPages);
        } else if (pagination?.totalCount) {
          setTotalPages(Math.ceil(pagination.totalCount / pageSize) || 1);
        } else if (pagination?.totalItems) {
          setTotalPages(Math.ceil(pagination.totalItems / pageSize) || 1);
        } else {
          setTotalPages(Math.ceil(data.length / pageSize) || 1);
        }
      } catch (err) {
        console.error('Greška pri učitavanju zaposlenih:', err);
        setError('Nije moguće učitati zaposlene. Pokušajte ponovo.');
        setEmployees([]);
      } finally {
        setLoading(false);
      }
  }, [searchTerm, employeeTypeFilter, areaFilter, page, pageSize, inactiveOnly]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Učitaj tipove zaposlenih i oblasti (za filtere i formu)
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [typesRes, areasRes] = await Promise.all([
          axiosInstance.get('/api/EmployeeType'),
          axiosInstance.get('/api/Area')
        ]);

        setEmployeeTypes(extractData(typesRes));
        setAreas(extractData(areasRes));
      } catch (err) {
        console.error('Greška pri učitavanju opcija:', err);
      }
    };

    fetchOptions();
  }, []);

  // Zatvori "tri tačke" meni klikom van njega
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(event.target)) {
        setIsOverflowMenuOpen(false);
      }
    };

    if (isOverflowMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOverflowMenuOpen]);

  // Prati promenu širine viewporta (mobilni <-> desktop raspored toolbar-a)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(min-width: 769px)');
    const handleChange = (event) => setIsDesktopView(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Stariji browseri (Safari < 14)
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  const handleDeleteEmployee = async (id) => {
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/api/Employee/${id}`);
      setEmployees(employees.filter(e => e.id !== id));
      setDeleteTarget(null);
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Zaposleni je uspešno obrisan'
      });
      // Osveži listu bez punog reload-a stranice.
      fetchEmployees();
    } catch (err) {
      console.error('Greška pri brisanju zaposlenog:', err);
      setDeleteTarget(null);
      setError('Nije moguće obrisati zaposlenog. Pokušajte ponovo.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getEmployeeTypeName = (employee) => {
    return employee?.employeeTypeName || '-';
  };

  const nextPage = () => {
    if (page < totalPages) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setEmployeeTypeFilter('');
    setAreaFilter('');
    setPage(1);
  };

  const handleCloseMobileFilter = () => {
    setIsFilterExiting(true);
    setTimeout(() => {
      setIsMobileFilterOpen(false);
      setIsFilterExiting(false);
    }, 300);
  };

  const hasActiveFilters = searchTerm || employeeTypeFilter || areaFilter;

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      address: '',
      hireDate: '',
      employeeTypeId: '',
      imgSrc: ''
    });
    setImageFile(null);
  };

  const openAddPanel = () => {
    setEditingEmployee(null);
    resetForm();
    setFormError(null);
    setStep(1);
    setIsSidePanelOpen(true);
  };

  const openEditPanel = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phoneNumber: employee.phoneNumber || employee.phone || '',
      address: employee.address || '',
      hireDate: employee.hireDate && !employee.hireDate.startsWith('0001-01-01')
        ? employee.hireDate.split('T')[0]
        : '',
      employeeTypeId: employee.employeeTypeId || '',
      imgSrc: employee.imgSrc || ''
    });
    setImageFile(null);
    setFormError(null);
    setStep(1);
    setEditSection('identity');
    setIsSidePanelOpen(true);
  };

  const closePanel = () => {
    setIsSidePanelOpen(false);
    setEditingEmployee(null);
    resetForm();
    setFormError(null);
    setStep(1);
    setEditSection('identity');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    // Validacija obaveznih polja
    if (!formData.employeeTypeId) {
      setFormLoading(false);
      setToast({
        show: true,
        kind: 'error',
        title: 'Greška',
        subtitle: 'Tip zaposlenog nije izabran. Molimo izaberite tip zaposlenog.'
      });
      setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
      return;
    }

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        hireDate: formData.hireDate ? new Date(formData.hireDate).toISOString() : null,
        employeeTypeId: formData.employeeTypeId
      };

      let response;
      let employeeId;
      if (editingEmployee) {
        // Update existing employee
        payload.id = editingEmployee.id;
        response = await axiosInstance.put(`/api/Employee/${editingEmployee.id}`, payload);
        employeeId = editingEmployee.id;
        console.log('Zaposleni ažuriran:', response.data);
      } else {
        // Create new employee
        response = await axiosInstance.post('/api/Employee', payload);
        employeeId = response.data?.data?.id || response.data?.id;
        console.log('Novi zaposleni kreiran:', response.data);
      }

      // Upload image if selected (requires employee id)
      if (imageFile && employeeId) {
        setIsImageUploading(true);
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', imageFile);
          await axiosInstance.post(
            `/api/Employee/${employeeId}/upload-image`,
            uploadFormData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
        } catch (uploadErr) {
          console.error('Greška pri uploadu slike:', uploadErr);
        } finally {
          setIsImageUploading(false);
        }
      }

      // Resetuj formu i zatvori panel
      resetForm();
      setEditingEmployee(null);
      setIsSidePanelOpen(false);

      // Prikaži notifikaciju i osveži stranicu
      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: editingEmployee ? 'Zaposleni je uspešno ažuriran' : 'Novi zaposleni je uspešno kreiran'
      });
      // Osveži listu bez punog reload-a stranice (reload bi na mobilnom
      // ponovo pokrenuo auth proveru i prikazao "Učitavanje..." ekran).
      fetchEmployees();
    } catch (err) {
      console.error('Greška pri čuvanju zaposlenog:', err);
      setFormError(err.response?.data?.message || 'Nije moguće sačuvati zaposlenog. Pokušajte ponovo.');
    } finally {
      setFormLoading(false);
    }
  };

  const paginatedEmployees = employees;

  // Paginacija (prethodna/sledeća stranica) - zajednička za toolbar (desktop)
  // i dno liste (mobilni). Na mobilnom ide ISPOD liste, pa se prosleđuje
  // dodatna klasa koja poništava "border-left"/"padding" iz toolbar varijante.
  const renderPagination = (extraClass = '') => (
    <div className={`employees-pagination ${extraClass}`.trim()}>
      <Button
        kind="ghost"
        size="md"
        renderIcon={ChevronLeft}
        onClick={prevPage}
        hasIconOnly
        disabled={page === 1}
        iconDescription="Prethodna stranica"
      />
      <span className="employees-pagination-label">
        {page}/{totalPages}
      </span>
      <Button
        kind="ghost"
        size="md"
        renderIcon={ChevronRight}
        onClick={nextPage}
        hasIconOnly
        disabled={page === totalPages}
        iconDescription="Sledeća stranica"
      />
    </div>
  );

  // "Tri tačke" (overflow meni) - zajednički za skeleton i glavno stanje.
  const overflowMenu = (
    <OverflowMenu
      isOpen={isOverflowMenuOpen}
      onToggle={() => setIsOverflowMenuOpen(prev => !prev)}
      onClose={() => setIsOverflowMenuOpen(false)}
      menuRef={overflowMenuRef}
      dark={isDarkMode}
    >
      <OverflowMenuItem
        icon={UserMultiple}
        onClick={() => {
          setIsOverflowMenuOpen(false);
          navigate('/employees/inactive');
        }}
      >
        Bivši zaposleni
      </OverflowMenuItem>
    </OverflowMenu>
  );

  // Akcije u toolbaru ("Dodaj zaposlenog" + "tri tačke") - prikazuju se samo
  // na desktopu. Na mobilnom nema dugmeta "Dodaj zaposlenog", a "tri tačke" se
  // prikazuju na mestu gde je ranije bila paginacija (u redu za pretragu).
  const toolbarActions = (
    <div className="employees-toolbar-actions">
      {canManageEmployees && (
        <Button
          kind="primary"
          renderIcon={Add}
          onClick={openAddPanel}
          className="add-employee-button"
        >
          Dodaj zaposlenog
        </Button>
      )}
      {overflowMenu}
    </div>
  );

  if (loading) {
    return (
      <div className="employees-grid-container">
        {/* Toolbar - real search bar and pagination */}
        <div className="employees-toolbar">
          <div className="employees-toolbar-search">
            <Search
              size="lg"
              placeholder="Pretražite zaposlene..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearchTerm('');
                setPage(1);
              }}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ flex: '1 1 250px', maxWidth: '400px' }}
            />
            <button
              type="button"
              onClick={() => setIsFilterOpen(prev => !prev)}
              className={`employees-filter-toggle ${isFilterOpen ? 'employees-filter-toggle-active' : ''}`}
              aria-label="Filteri"
              aria-expanded={isFilterOpen}
            >
              <Filter size={20} />
            </button>

            {isDesktopView ? renderPagination() : overflowMenu}
          </div>

          {isDesktopView && toolbarActions}
        </div>

        {/* Grid Skeleton - desktop */}
        <div
          className="employees-skeleton-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '2rem'
          }}
        >
          {[...Array(4)].map((_, index) => (
            <div key={index} className="employee-card employee-card-skeleton">
              <div className="employee-card-avatar">
                <SkeletonPlaceholder
                  className="employee-skeleton-avatar"
                  style={{
                    width: '100%',
                    height: '100%'
                  }}
                />
              </div>
              <div className="employee-card-content">
                <SkeletonText style={{ width: '70%', height: '16px', margin: '0 auto 0.5rem' }} />
                <div className="employee-card-details">
                  <div className="employee-card-detail">
                    <SkeletonText style={{ width: '40%', height: '12px' }} />
                    <SkeletonText style={{ width: '40%', height: '12px' }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobilni: paginacija ispod liste */}
        {!isDesktopView && renderPagination('employees-pagination--bottom')}
      </div>
    );
  }

  if (error) {
    return (
      <>
        <div style={{
          position: 'fixed',
          top: '4rem',
          right: '1rem',
          zIndex: 9999,
          minWidth: '320px',
          animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <style>
            {`
              @keyframes slideInFromRight {
                from {
                  transform: translateX(calc(100% + 1rem));
                  opacity: 0;
                }
                to {
                  transform: translateX(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          <InlineNotification
            kind="error"
            title="Greška"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
            hideCloseButton={false}
            lowContrast
          />
        </div>
        <div className="employees-grid-error" />
      </>
    );
  }

  return (
    <div className="employees-grid-container">
      {/* Toolbar */}
      <div className="employees-toolbar">
        <div className="employees-toolbar-search">
          <Search
            size="lg"
            placeholder="Pretražite zaposlene..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearchTerm('');
              setPage(1);
            }}
            labelText=""
            closeButtonLabelText="Obriši pretragu"
            style={{ flex: '1 1 250px', maxWidth: '400px' }}
          />
          <button
            type="button"
            onClick={() => setIsFilterOpen(prev => !prev)}
            className={`employees-filter-toggle ${isFilterOpen ? 'employees-filter-toggle-active' : ''}`}
            aria-label="Filteri"
            aria-expanded={isFilterOpen}
          >
            <Filter size={20} />
          </button>

          {isDesktopView ? renderPagination() : overflowMenu}
        </div>

        <div className={`employees-filters ${isFilterOpen ? 'employees-filters-open' : ''}`}>
          <Select
            id="employee-type-filter"
            labelText=""
            value={employeeTypeFilter}
            onChange={(e) => {
              setEmployeeTypeFilter(e.target.value);
              setPage(1);
            }}
            size="lg"
            style={{ minWidth: '160px' }}
          >
            <SelectItem value="" text="Svi tipovi" />
            {employeeTypes.map((type) => (
              <SelectItem key={type.id} value={type.id} text={type.name} />
            ))}
          </Select>

          <Select
            id="area-filter"
            labelText=""
            value={areaFilter}
            onChange={(e) => {
              setAreaFilter(e.target.value);
              setPage(1);
            }}
            size="lg"
            style={{ minWidth: '160px' }}
          >
            <SelectItem value="" text="Sve oblasti" />
            {areas.map((area) => (
              <SelectItem key={area.id} value={area.id} text={area.name} />
            ))}
          </Select>

          {hasActiveFilters && (
            <Button
              kind="ghost"
              size="md"
              renderIcon={Close}
              onClick={clearFilters}
              className="clear-filters-button"
            >
              Obriši filtere
            </Button>
          )}
        </div>

        {isDesktopView && toolbarActions}
      </div>

      {/* Grid */}
      {paginatedEmployees.length === 0 ? (
        <div className="employees-empty">
          <div className="empty-icon">👥</div>
          <p className="empty-title">{inactiveOnly ? 'Nema bivših zaposlenih' : 'Nema zaposlenih'}</p>
          <p className="empty-subtitle">
            {searchTerm
              ? 'Pokušajte sa drugom pretragom'
              : (inactiveOnly ? 'Ovde se prikazuju deaktivirani zaposleni' : 'Dodajte prvog zaposlenog')}
          </p>
        </div>
      ) : (
        <div
          className="employees-grid"
          style={{
            opacity: pageFading ? 0 : 1,
            transition: 'opacity 0.3s ease',
            animation: 'fadeIn 0.4s ease-in'
          }}
        >
          {paginatedEmployees.map((employee) => {
            const initials = getInitials(employee.firstName, employee.lastName);
            const hasImage = employee.imgSrc && employee.imgSrc !== '';

            return (
              <div
                key={employee.id}
                className={`employee-card ${inactiveOnly ? 'employee-card--inactive' : ''}`}
                onClick={() => navigate(`/employees/${employee.id}`)}
              >
                {/* Avatar */}
                <div className="employee-card-avatar">
                  {hasImage ? (
                    <>
                      <img
                        src={employee.imgSrc}
                        alt={`${employee.firstName} ${employee.lastName}`}
                        className="employee-card-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div
                        className="employee-card-initials"
                        style={{ display: 'none' }}
                      >
                        {initials || '?'}
                      </div>
                    </>
                  ) : (
                    <div className="employee-card-initials">
                      {initials || '?'}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="employee-card-content">
                  <h3 className="employee-card-name">
                    {employee.firstName} {employee.lastName}
                  </h3>

                  <div className="employee-card-details">
                    <div className="employee-card-detail">
                      <span className="detail-label">Tip zaposlenog:</span>
                      <span className="detail-value">{getEmployeeTypeName(employee)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobilni: paginacija ispod liste */}
      {!isDesktopView && paginatedEmployees.length > 0 && renderPagination('employees-pagination--bottom')}

      {/* Right Side Panel */}
      <div className={`employee-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
        <div className="side-panel-header" style={{ borderBottom: 'none', padding: '1rem 1.5rem 0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500', color: 'var(--cds-text-primary)' }}>
            {editingEmployee ? 'Izmeni zaposlenog' : 'Dodaj zaposlenog'}
          </h3>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Close}
            iconDescription="Zatvori"
            hasIconOnly
            onClick={closePanel}
          />
        </div>

        {formError && (
          <div style={{
            position: 'fixed',
            top: '4rem',
            right: '1rem',
            zIndex: 9999,
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <style>
              {`
                @keyframes slideInFromRight {
                  from {
                    transform: translateX(calc(100% + 1rem));
                    opacity: 0;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }
              `}
            </style>
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={formError}
              onCloseButtonClick={() => setFormError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        )}

        <div className="side-panel-content">
          <form onSubmit={(e) => e.preventDefault()} className="employee-form">
            {editingEmployee ? (
              <>
                {/* Edit mode: step indicator (same as add mode) */}
                <div className="custom-progress-bar">
                  <div className="progress-steps">
                    <div className={`progress-step ${step >= 1 ? 'completed' : ''} ${step === 1 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 1 ? <Checkmark size={16} /> : '1'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Lični podaci</div>
                      </div>
                    </div>
                    <div className={`progress-line ${step >= 2 ? 'completed' : ''}`}></div>
                    <div className={`progress-step ${step >= 2 ? 'completed' : ''} ${step === 2 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 2 ? <Checkmark size={16} /> : '2'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Posao</div>
                      </div>
                    </div>
                    <div className={`progress-line ${step >= 3 ? 'completed' : ''}`}></div>
                    <div className={`progress-step ${step >= 3 ? 'completed' : ''} ${step === 3 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 3 ? <Checkmark size={16} /> : '3'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Slika</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 1: Lični podaci */}
                {step === 1 && (
                  <div className="employee-form-step-content">
                    <TextInput
                      id="firstName"
                      labelText="Ime *"
                      placeholder="Unesite ime"
                      value={formData.firstName}
                      onChange={(e) => handleFormChange('firstName', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="lastName"
                      labelText="Prezime *"
                      placeholder="Unesite prezime"
                      value={formData.lastName}
                      onChange={(e) => handleFormChange('lastName', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="email"
                      labelText="Email"
                      placeholder="email@primer.com"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="phoneNumber"
                      labelText="Telefon"
                      placeholder="+381 ..."
                      value={formData.phoneNumber}
                      onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="address"
                      labelText="Adresa"
                      placeholder="Unesite adresu"
                      value={formData.address}
                      onChange={(e) => handleFormChange('address', e.target.value)}
                      disabled={formLoading}
                    />
                  </div>
                )}

                {/* Step 2: Posao */}
                {step === 2 && (
                  <div className="employee-form-step-content">
                    <TextInput
                      id="hireDate"
                      labelText="Datum zaposlenja"
                      type="date"
                      value={formData.hireDate}
                      onChange={(e) => handleFormChange('hireDate', e.target.value)}
                      disabled={formLoading}
                    />
                    <Select
                      id="employeeTypeId"
                      labelText="Tip zaposlenog *"
                      value={formData.employeeTypeId}
                      onChange={(e) => handleFormChange('employeeTypeId', e.target.value)}
                      disabled={formLoading}
                    >
                      <SelectItem value="" text="Izaberite tip" />
                      {employeeTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id} text={type.name} />
                      ))}
                    </Select>
                  </div>
                )}

                {/* Step 3: Slika */}
                {step === 3 && (
                  <div className="employee-form-step-content">
                    <div>
                      <label
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          marginBottom: '0.5rem',
                          color: 'var(--cds-text-secondary)'
                        }}>
                        Fotografija (opciono)
                      </label>
                      <FileUploader
                        labelTitle=""
                        labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                        buttonLabel="Izaberi sliku"
                        filenameStatus="edit"
                        accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                        multiple={false}
                        disabled={formLoading || isImageUploading}
                        onChange={(e) => setImageFile(e.target.files[0])}
                      />
                      {isImageUploading && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                          Upload slike u toku...
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="employee-form-nav">
                  {step > 1 && (
                    <Button
                      kind="secondary"
                      type="button"
                      onClick={() => setStep(prev => prev - 1)}
                      disabled={formLoading}
                    >
                      Nazad
                    </Button>
                  )}
                  {step < 3 ? (
                    <Button
                      kind="primary"
                      type="button"
                      onClick={() => setStep(prev => prev + 1)}
                      className="add-employee-button"
                    >
                      Dalje
                    </Button>
                  ) : (
                    <Button
                      kind="primary"
                      type="button"
                      onClick={handleSubmit}
                      disabled={formLoading || isImageUploading}
                      className="add-employee-button"
                    >
                      {formLoading ? 'Čuvanje...' : 'Sačuvaj izmene'}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Add mode: step indicator */}
                <div className="custom-progress-bar">
                  <div className="progress-steps">
                    <div className={`progress-step ${step >= 1 ? 'completed' : ''} ${step === 1 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 1 ? <Checkmark size={16} /> : '1'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Lični podaci</div>
                      </div>
                    </div>
                    <div className={`progress-line ${step >= 2 ? 'completed' : ''}`}></div>
                    <div className={`progress-step ${step >= 2 ? 'completed' : ''} ${step === 2 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 2 ? <Checkmark size={16} /> : '2'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Posao</div>
                      </div>
                    </div>
                    <div className={`progress-line ${step >= 3 ? 'completed' : ''}`}></div>
                    <div className={`progress-step ${step >= 3 ? 'completed' : ''} ${step === 3 ? 'active' : ''}`}>
                      <div className="step-circle">
                        {step > 3 ? <Checkmark size={16} /> : '3'}
                      </div>
                      <div className="step-label">
                        <div className="step-title">Slika</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 1: Lični podaci */}
                {step === 1 && (
                  <div className="employee-form-step-content">
                    <TextInput
                      id="firstName"
                      labelText="Ime *"
                      placeholder="Unesite ime"
                      value={formData.firstName}
                      onChange={(e) => handleFormChange('firstName', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="lastName"
                      labelText="Prezime *"
                      placeholder="Unesite prezime"
                      value={formData.lastName}
                      onChange={(e) => handleFormChange('lastName', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="email"
                      labelText="Email"
                      placeholder="email@primer.com"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="phoneNumber"
                      labelText="Telefon"
                      placeholder="+381 ..."
                      value={formData.phoneNumber}
                      onChange={(e) => handleFormChange('phoneNumber', e.target.value)}
                      disabled={formLoading}
                    />
                    <TextInput
                      id="address"
                      labelText="Adresa"
                      placeholder="Unesite adresu"
                      value={formData.address}
                      onChange={(e) => handleFormChange('address', e.target.value)}
                      disabled={formLoading}
                    />
                  </div>
                )}

                {/* Step 2: Posao */}
                {step === 2 && (
                  <div className="employee-form-step-content">
                    <TextInput
                      id="hireDate"
                      labelText="Datum zaposlenja"
                      type="date"
                      value={formData.hireDate}
                      onChange={(e) => handleFormChange('hireDate', e.target.value)}
                      disabled={formLoading}
                    />
                    <Select
                      id="employeeTypeId"
                      labelText="Tip zaposlenog *"
                      value={formData.employeeTypeId}
                      onChange={(e) => handleFormChange('employeeTypeId', e.target.value)}
                      disabled={formLoading}
                    >
                      <SelectItem value="" text="Izaberite tip" />
                      {employeeTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id} text={type.name} />
                      ))}
                    </Select>
                  </div>
                )}

                {/* Step 3: Slika */}
                {step === 3 && (
                  <div className="employee-form-step-content">
                    <div>
                      <label
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          marginBottom: '0.5rem',
                          color: 'var(--cds-text-secondary)'
                        }}>
                        Fotografija (opciono)
                      </label>
                      <FileUploader
                        labelTitle=""
                        labelDescription="Samo JPG, PNG, WEBP, GIF, BMP (maks 5MB)"
                        buttonLabel="Izaberi sliku"
                        filenameStatus="edit"
                        accept={['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']}
                        multiple={false}
                        disabled={formLoading || isImageUploading}
                        onChange={(e) => setImageFile(e.target.files[0])}
                      />
                      {isImageUploading && (
                        <p style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.5rem' }}>
                          Upload slike u toku...
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation buttons */}
                <div className="employee-form-nav">
                  {step > 1 && (
                    <Button
                      kind="secondary"
                      type="button"
                      onClick={() => setStep(prev => prev - 1)}
                      disabled={formLoading}
                    >
                      Nazad
                    </Button>
                  )}
                  {step < 3 ? (
                    <Button
                      kind="primary"
                      type="button"
                      onClick={() => setStep(prev => prev + 1)}
                      className="add-employee-button"
                    >
                      Dalje
                    </Button>
                  ) : (
                    <Button
                      kind="primary"
                      type="button"
                      onClick={handleSubmit}
                      disabled={formLoading || isImageUploading}
                      className="add-employee-button"
                    >
                      {formLoading ? 'Čuvanje...' : 'Sačuvaj'}
                    </Button>
                  )}
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {/* Overlay */}
      {isSidePanelOpen && (
        <div
          className="side-panel-overlay"
          onClick={closePanel}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        modalHeading="Obriši zaposlenog"
        primaryButtonText={deleteLoading ? 'Brisanje...' : 'Obriši'}
        secondaryButtonText="Otkaži"
        danger
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={() => deleteTarget && handleDeleteEmployee(deleteTarget.id)}
        primaryButtonDisabled={deleteLoading}
      >
        <p>
          Da li ste sigurni da želite da obrišete zaposlenog{' '}
          <strong>{deleteTarget ? `${deleteTarget.firstName} ${deleteTarget.lastName}` : ''}</strong>?
        </p>
      </Modal>

      {/* Toast Notification */}
      {toast.show && (
        <div className="toast-container">
          <ToastNotification
            kind={toast.kind}
            title={toast.title}
            subtitle={toast.subtitle}
            timeout={0}
            onClose={() => setToast(prev => ({ ...prev, show: false }))}
            lowContrast
          />
        </div>
      )}

      {/* Mobile Filter Button */}
      {ReactDOM.createPortal(
        <button
          className="floating-button employee-mobile-filter-btn"
          onClick={() => setIsMobileFilterOpen(true)}
          style={{
            position: 'fixed',
            bottom: '2rem',
            left: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9998
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Filter
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Filteri"
            color="#ffffff"
          />
          <span style={{
            color: '#ffffff',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Filteri
          </span>
        </button>,
        document.body
      )}

      {/* Mobile Filter Modal */}
      {isMobileFilterOpen && ReactDOM.createPortal(
        <div
          className={isFilterExiting ? 'filter-modal-exiting' : ''}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDarkMode ? 'rgba(22, 22, 22, 0.85)' : 'rgba(244, 244, 244, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'flex-end',
            animation: isFilterExiting ? 'fadeOut 0.2s ease-out' : 'fadeIn 0.2s ease-out'
          }}
          onClick={handleCloseMobileFilter}
        >
          <div
            className="employee-mobile-filter-panel"
            data-carbon-theme={theme}
            style={{
              width: '100%',
              backgroundColor: isDarkMode ? '#262626' : '#ffffff',
              borderRadius: '16px 16px 0 0',
              padding: '1.5rem',
              animation: isFilterExiting ? 'slideDownOut 0.3s ease-out' : 'slideUpIn 0.3s ease-out',
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '500', color: isDarkMode ? '#ffffff' : '#161616' }}>Filteri</h3>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                className="employee-mobile-filter-close-btn"
                renderIcon={Close}
                iconDescription="Zatvori"
                onClick={handleCloseMobileFilter}
                style={{ color: isDarkMode ? '#ffffff' : '#161616' }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Select
                id="mobile-employee-type-filter"
                labelText="Tip zaposlenog"
                value={employeeTypeFilter}
                onChange={(e) => {
                  setEmployeeTypeFilter(e.target.value);
                  setPage(1);
                }}
                size="md"
              >
                <SelectItem value="" text="Svi tipovi" />
                {employeeTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id} text={type.name} />
                ))}
              </Select>

              <Select
                id="mobile-area-filter"
                labelText="Oblast"
                value={areaFilter}
                onChange={(e) => {
                  setAreaFilter(e.target.value);
                  setPage(1);
                }}
                size="md"
              >
                <SelectItem value="" text="Sve oblasti" />
                {areas.map((area) => (
                  <SelectItem key={area.id} value={area.id} text={area.name} />
                ))}
              </Select>

              <Button
                kind="primary"
                size="md"
                style={{ width: '100%', marginTop: '1rem' }}
                onClick={handleCloseMobileFilter}
              >
                Primeni filtere
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Floating Action Button (mobile only) */}
      {canManageEmployees && !isSidePanelOpen && ReactDOM.createPortal(
        <button
          className="floating-button employee-fab"
          onClick={openAddPanel}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            height: '56px',
            padding: '0 24px',
            backgroundColor: '#9fb746',
            border: 'none',
            borderRadius: '28px',
            display: 'none',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'none',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: '12px',
            outline: 'none',
            zIndex: 9999
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#8da63d';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#9fb746';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
          onMouseDown={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
          }}
          onMouseUp={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
          }}
        >
          <Add
            size={24}
            style={{
              flexShrink: 0,
              minWidth: '24px',
              minHeight: '24px',
              fill: '#ffffff'
            }}
            aria-label="Dodaj zaposlenog"
            color="#ffffff"
          />
          <span style={{
            color: 'white',
            fontWeight: '500',
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            Dodaj zaposlenog
          </span>
        </button>,
        document.body
      )}
    </div>
  );
};

export default EmployeesTable;
